import React, {useState, useContext, useEffect, useRef} from 'react';
import "firebase/compat/analytics";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import CharacterContext from '../context/CharacterContext';
import UserContext from '../context/UserContext';
import CampaignContext from '../context/CampaignContext';
import '../styles/inventory.css'
import { PencilIcon, TrashIcon, CheckIcon, AnnotationIcon } from '@heroicons/react/solid'
import { SearchIcon } from '@heroicons/react/outline'
import i18next from 'i18next';
import {dynamicSortInventory} from '../utils/sort';
import {generateUpdateHisto} from '../utils/dice';

// Les changements de quantité (± / saisie) sont regroupés avant d'écrire en
// base, pour ne pas générer une ligne d'historique par clic.
const SAVE_DELAY = 800;

const Inventory = (props) => {
  const {user} = useContext(UserContext);
  const {character} = useContext(CharacterContext);
  const {campaign} = useContext(CampaignContext);

  const [itemName, setItemName] = useState("")
  const [numberOfnewItem, setNumberOfnewItem] = useState('')
  const [searchQuery, setSearchQuery] = useState('');
  const [defaultDataCharacter, setDefaultDataCharacter] = useState({});

  const [editNameIndex, setEditNameIndex] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [editQtyIndex, setEditQtyIndex] = useState(null);
  const [qtyDraft, setQtyDraft] = useState('');
  const [openNoteIndex, setOpenNoteIndex] = useState(null);
  const [noteValue, setNoteValue] = useState('');

  // force le re-render après une mutation en place de character.inventory
  const [, setTick] = useState(0);
  const rerender = () => setTick(t => t + 1);

  // toujours lire les dernières valeurs dans les sauvegardes différées
  const saveTimer = useRef(null);
  const latest = useRef({});
  latest.current = { character, defaultDataCharacter, campaign, user, updateInventory: props.updateInventory };

  useEffect( () => {
    setDefaultDataCharacter(JSON.parse(JSON.stringify({...character})))
  }, [character]);

  const persist = () => {
    const l = latest.current;
    l.updateInventory(l.character, generateUpdateHisto(l.character, l.defaultDataCharacter, l.campaign, l.user));
  };

  const persistNow = () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    persist();
  };

  const persistDebounced = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      persist();
    }, SAVE_DELAY);
  };

  // flush d'une sauvegarde en attente si on quitte l'inventaire
  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
        persist();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = (item) => (item.type === 'alchemy' && item.default)
    ? i18next.t(`inventoryItem.${item.name}`)
    : item.name;

  const createItem = () => {
    if (!character.inventory) character.inventory = [];
    character.inventory.push({ name: itemName, number: numberOfnewItem || 1 });
    persistNow();
  }

  const changeQty = (realIndex, delta) => {
    const cur = Number(character.inventory[realIndex].number) || 0;
    character.inventory[realIndex].number = Math.max(0, cur + delta);
    rerender();
    persistDebounced();
  }

  const commitQty = (realIndex) => {
    const val = Math.max(0, parseInt(qtyDraft, 10) || 0);
    character.inventory[realIndex].number = val;
    setEditQtyIndex(null);
    rerender();
    persistNow();
  }

  const saveName = (realIndex) => {
    const val = (nameDraft || '').trim();
    if (val) {
      character.inventory[realIndex].name = val;
      persistNow();
    }
    setEditNameIndex(null);
    rerender();
  }

  const removeItem = (realIndex) => {
    setOpenNoteIndex(null);
    setEditNameIndex(null);
    setEditQtyIndex(null);
    character.inventory.splice(realIndex, 1);
    persistNow();
  }

  const saveNote = (realIndex) => {
    character.inventory[realIndex] = { ...character.inventory[realIndex], note: noteValue };
    setOpenNoteIndex(null);
    persistNow();
  }

  const renderItem = ({ item, realIndex }) => {
    const isAlchemy = !!item.default;
    const noteOpen = openNoteIndex === realIndex;
    const nameEditing = editNameIndex === realIndex && !isAlchemy;
    const qtyEditing = editQtyIndex === realIndex;
    return (
      <div key={realIndex} className='invItem'>
        <div className='invItemMain'>
          <div className='invQty'>
            <button type='button' className='invQtyBtn' aria-label='-' onClick={() => changeQty(realIndex, -1)}>–</button>
            {qtyEditing ? (
              <input
                className='invQtyInput'
                type='number'
                min={0}
                autoFocus
                value={qtyDraft}
                onChange={(e) => setQtyDraft(e.target.value)}
                onBlur={() => commitQty(realIndex)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitQty(realIndex); }
                  if (e.key === 'Escape') { setEditQtyIndex(null); }
                }}
              />
            ) : (
              <span
                className='invQtyVal'
                title={i18next.t('editQuantity')}
                onClick={() => { setQtyDraft(String(item.number != null ? item.number : 0)); setEditQtyIndex(realIndex); }}
              >×{item.number}</span>
            )}
            <button type='button' className='invQtyBtn' aria-label='+' onClick={() => changeQty(realIndex, 1)}>+</button>
          </div>

          <div className='invItemBody'>
            {nameEditing ? (
              <input
                className='invEditInput'
                type='text'
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={() => saveName(realIndex)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); saveName(realIndex); }
                  if (e.key === 'Escape') { setEditNameIndex(null); }
                }}
              />
            ) : (
              <span className='invItemName' title={label(item)}>{label(item)}</span>
            )}
            {!noteOpen && item.note && (
              <div className='invItemNote'>
                <AnnotationIcon className='invNoteIcon' />
                <span>{item.note}</span>
              </div>
            )}
          </div>

          <div className='invItemActions'>
            {!isAlchemy && !nameEditing && (
              <button
                type='button'
                className='optionBtnInv'
                aria-label='edit'
                onClick={() => { setNameDraft(item.name); setEditNameIndex(realIndex); }}
              >
                <PencilIcon className='iconInv' />
              </button>
            )}
            {!isAlchemy && nameEditing && (
              <button type='button' className='optionBtnInv' aria-label='save' onClick={() => saveName(realIndex)}>
                <CheckIcon className='iconInv' />
              </button>
            )}
            <button
              type='button'
              className={`optionBtnInv ${noteOpen ? 'active' : ''} ${item.note ? 'hasNote' : ''}`}
              aria-label='note'
              onClick={() => {
                if (noteOpen) { setOpenNoteIndex(null); }
                else { setNoteValue(item.note || ''); setOpenNoteIndex(realIndex); }
              }}
            >
              <AnnotationIcon className='iconInv' />
            </button>
            {!isAlchemy && (
              <button type='button' className='optionBtnInv del' aria-label='delete' onClick={() => removeItem(realIndex)}>
                <TrashIcon className='iconInv' />
              </button>
            )}
          </div>
        </div>

        {noteOpen && (
          <div className='invNoteRow'>
            <textarea
              className='invNoteInput'
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              placeholder={i18next.t('note')}
              autoFocus
              rows={2}
            />
            <button type='button' className='optionBtnInv' aria-label='save note' onClick={() => saveNote(realIndex)}>
              <CheckIcon className='iconInv' />
            </button>
          </div>
        )}
      </div>
    );
  };

  if(character.uid) {
    const cmp = dynamicSortInventory('name', 'inv');
    const visible = (character.inventory || [])
      .map((item, realIndex) => ({ item, realIndex }))
      .filter(({ item }) => label(item).toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => cmp(a.item, b.item));
    const mainItems = visible.filter(({ item }) => !item.default);
    const alchemyItems = visible.filter(({ item }) => item.default);

    return (
      <div className='containerInv'>
        <p className='titleSection'><b>{i18next.t('inventory')}</b></p>
        <div className='searchInv'>
          <SearchIcon className='searchInvIcon' />
          <input
            type='text'
            placeholder={i18next.t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className='invTableWrapper'>
          {visible.length === 0 && (
            <p className='invEmpty'>
              {searchQuery ? i18next.t('inventoryNoResult') : i18next.t('inventoryEmpty')}
            </p>
          )}
          {mainItems.length > 0 && (
            <div className='invGrid'>{mainItems.map(renderItem)}</div>
          )}
          {alchemyItems.length > 0 && (
            <>
              <p className='invGroupLabel'>{i18next.t('alchemy.title')}</p>
              <div className='invGrid'>{alchemyItems.map(renderItem)}</div>
            </>
          )}
        </div>
        <form className='formNewInv' onSubmit={(e) => {
          e.preventDefault();
          if(itemName) {
            createItem();
          }
          setItemName('');
          setNumberOfnewItem('');
        }}>
          <input
            name="newItemInventory"
            type="text"
            className='fName'
            placeholder={i18next.t('item name')}
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
          <input
            name="numberOfNewItem"
            type="number"
            className='fQty'
            placeholder={i18next.t('number of item')}
            value={numberOfnewItem}
            onChange={(e) => setNumberOfnewItem(e.target.value ? JSON.parse(e.target.value) : '')}
          />
          <input className={itemName === "" ? 'disabled' : 'outline'} type="submit" value={i18next.t('create')} />
        </form>
      </div>
    )
  }
  return null;
}

export default Inventory
